import { Card, Col, Row, Skeleton, Space } from "antd";
import {
  VuiCheckbox,
  VuiFormCard,
  VuiPagination,
  VuiSearch,
  VuiSelectSingle,
} from "../../../../@vendor/components";
import CategoryRepository from "../../../../repositories/CategoryRepository";
import PsychologistCard from "../../../psychologist/components/psychologist-card";
import { NoContentCard } from "../../../../components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useBookingCounseling } from "../../providers/booking-counseling.provider";
import { useTranslation } from "react-i18next";
import { useDebounce, useWindowSize } from "usehooks-ts";
import _ from "lodash";
import { useGetPsychologists } from "../../../psychologist/hooks/use-get-psychologists";
import { useGetExpertises } from "../../../expertise/hooks/use-get-expertises";
import FilterExpertise from "../filter-expertise";
import { Psychologist } from "../../../../models/Psychologist";

const BookingCounselingStepPsychologist = () => {
  const { t } = useTranslation();
  const { currentStep, form, toStep } = useBookingCounseling();
  const { values, setFieldValue, setFormValues } = form;
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [isGoNextStep, setIsGoNextStep] = useState<boolean>(false);
  const [tempPsychologistId, setTempPsychologistId] = useState(null);
  const [tableQuery, setTableQuery] = useState<{
    limit: number;
    page: number;
    expertise_ids: number[];
  }>({
    limit: 6,
    page: 1,
    expertise_ids: [],
  });

  const onPageChange = useCallback((page: number, pageSize: number) => {
    setTableQuery((prevState) => ({
      ...prevState,
      page: page,
      limit: pageSize,
    }));
  }, []);

  const onChangeExpertise = useCallback(
    (value: boolean, expertiseId: number) => {
      setTableQuery((prevState) => {
        let expertises: number[] = [...prevState.expertise_ids];

        if (value) {
          expertises.push(expertiseId);
        } else {
          expertises = expertises.filter((e) => e !== expertiseId);
        }

        return {
          ...prevState,
          page: 1,
          expertise_ids: _.uniq(expertises),
        };
      });
    },
    []
  );

  const {
    data: psychologists,
    loadData: loadPsychologistData,
    isLoading: isLoadingPsychologist,
    totalData: totalPsychologistData,
  } = useGetPsychologists();

  const {
    data: expertises,
    loadData: loadExpertiseData,
    isLoading: isLoadingExpertise,
  } = useGetExpertises();

  const [search, setSearch] = useState<string>("");
  const debounceSearch = useDebounce(search, 300);
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const checkPsychologist = async (categoryID: string) => {
    await loadPsychologistData({
      // page: tableQuery.page,
      // per_page: tableQuery.limit,
      category: categoryID,
      expertises: tableQuery.expertise_ids,
      search: debounceSearch,
      psychologist_id: values?.psychologist_id,
    }).then((resp: any) => {
      if (values?.psychologist_id) {
        const psychologist = resp?.find(
          (p: Psychologist) => p?.id == values?.psychologist_id
        );
        setFormValues({
          type: "Online",
          session: "single-session",
          discount: 0,
          customer: null,
          schedule_id: null,
          schedule_dates: [],
          schedule_date: null,
          schedule_times: [],
          schedule_duration: null,
          schedule_ids: [],
          schedules: [],
          time_ids: [],
          time: [],
          patient: null,
        });
        if (psychologist) {
          setFieldValue("psychologist", psychologist);
          toStep(1);
        } else {
          setFieldValue("psychologist", null);
          setFieldValue("psychologist_id", null);
        }
      }
    });
  };

  useMemo(() => {
    loadExpertiseData().then();
  }, []);

  useEffect(() => {
    loadPsychologistData({
      // page: tableQuery.page,
      // per_page: tableQuery.limit,
      category: values?.category_id,
      expertises: tableQuery.expertise_ids,
      search: debounceSearch,
      psychologist_id: values?.psychologist_id,
    }).then();
  }, [tableQuery, values?.psychologist_id, debounceSearch]);

  useEffect(() => {
    if (
      values?.psychologist_id &&
      !values?.psychologist &&
      psychologists?.length
    ) {
      const psychologist = psychologists?.find(
        (p) => p?.id == values?.psychologist_id
      );
      setFieldValue("psychologist", psychologist);
    }
  }, [values?.psychologist_id, values?.psychologist, psychologists]);

  if (currentStep !== 0) {
    return null;
  }

  return (
    <Row gutter={[20, 48]}>
      <Col md={10} sm={24}>
        <div className="form-group">
          <label style={{ fontSize: 24 }}>
            {t("common.text.chooseCounselingCategory")}
          </label>
          <VuiSelectSingle
            placeholder={t("common.text.chooseCategory")}
            repository={CategoryRepository}
            value={values.category}
            onChange={(event) => {
              setFieldValue("category_id", event.value);
              setFieldValue("category", {
                name: event?.label,
                label: event?.label,
                id: event?.value,
                value: event?.value,
              });
              checkPsychologist(event.value as string).then();
            }}
          />
        </div>
      </Col>

      <Col md={24} sm={24}>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <Row gutter={[20, 20]} justify="space-between" align="middle">
            <Col md={14} xs={24}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>
                {t("common.text.chooseYourPsychologist")}
              </div>
            </Col>

            {isMobile ? (
              <Col xs={24}>
                <Space size={8} style={{ width: "100%" }}>
                  <VuiSearch
                    placeholder={t("common.text.searchPsychologist")}
                    value={search}
                    onChange={onSearchChange}
                  />
                  <FilterExpertise
                    expertises={expertises}
                    selectedData={tableQuery?.expertise_ids}
                    onChange={(value) => {
                      setTableQuery({
                        page: 1,
                        limit: 6,
                        expertise_ids: value,
                      });
                    }}
                  />
                </Space>
              </Col>
            ) : (
              <Col xs={10}>
                <VuiSearch
                  placeholder={t("common.text.searchPsychologist")}
                  value={search}
                  onChange={onSearchChange}
                />
              </Col>
            )}
          </Row>

          <Row gutter={[20, 20]}>
            <Col className="hide-mobile" md={8} sm={24}>
              <Card className="vui-form-card">
                <Space size={16} direction="vertical" style={{ width: "100%" }}>
                  <div
                    style={{
                      fontSize: 18,
                      color: "#4F5762",
                      fontWeight: 600,
                    }}
                  >
                    {t("common.text.filterExpertises")}
                  </div>

                  {isLoadingExpertise ? (
                    <div style={{ width: "100%" }}>
                      <Skeleton active paragraph={{ rows: 4 }} />
                    </div>
                  ) : (
                    expertises.map((expertise, index) => (
                      <VuiCheckbox
                        key={`expertise_${index}`}
                        value={expertise.id}
                        checked={tableQuery?.expertise_ids?.includes(
                          expertise.id
                        )}
                        title={expertise.name}
                        onChange={(e) => {
                          onChangeExpertise(e.target.checked, expertise.id);
                        }}
                      />
                    ))
                  )}
                </Space>
              </Card>
            </Col>

            <Col md={16} sm={24}>
              <Row gutter={[20, 20]}>
                {isLoadingPsychologist ? (
                  <Col xs={24}>
                    <Row gutter={[20, 20]}>
                      <Col xs={24}>
                        <VuiFormCard>
                          <Skeleton avatar active paragraph={{ rows: 4 }} />
                        </VuiFormCard>
                      </Col>
                      <Col xs={24}>
                        <VuiFormCard>
                          <Skeleton avatar active paragraph={{ rows: 4 }} />
                        </VuiFormCard>
                      </Col>
                      <Col xs={24}>
                        <VuiFormCard>
                          <Skeleton avatar active paragraph={{ rows: 4 }} />
                        </VuiFormCard>
                      </Col>
                    </Row>
                  </Col>
                ) : psychologists.length ? (
                  psychologists.map((item) => (
                    <Col xs={24} key={`psychologist_${item.id}`}>
                      <PsychologistCard
                        data={item}
                        isSelected={values.psychologist_id === item.id}
                        onSelect={(p) => {
                          setFieldValue("psychologist_id", p?.id);
                          setFieldValue("psychologist", p);
                          if (values?.category_id) {
                            setFormValues({
                              type: "Online",
                              session: "single-session",
                              discount: 0,
                              customer: null,
                              schedule_id: null,
                              schedule_dates: [],
                              schedule_date: null,
                              schedule_times: [],
                              schedule_duration: null,
                              schedule_ids: [],
                              schedules: [],
                              time_ids: [],
                              time: [],
                              patient: null,
                            });
                            toStep(1);
                          }
                        }}
                        nearestSchedule={item.nearest_counseling_schedule_date}
                      />
                    </Col>
                  ))
                ) : (
                  <Col xs={24}>
                    <NoContentCard title={t("common.text.psychologist")} />
                  </Col>
                )}

                {/*{!isLoadingPsychologist && (*/}
                {/*  <Col xs={24} style={{ textAlign: "center", marginTop: 8 }}>*/}
                {/*    <VuiPagination*/}
                {/*      onChange={onPageChange}*/}
                {/*      total={totalPsychologistData}*/}
                {/*      current={tableQuery.page}*/}
                {/*      pageSize={tableQuery.limit}*/}
                {/*    />*/}
                {/*  </Col>*/}
                {/*)}*/}
              </Row>
            </Col>
          </Row>
        </Space>
      </Col>
    </Row>
  );
};

export default BookingCounselingStepPsychologist;
